import { Link } from "@flat-ui";
import { Stack, Box } from "@mui-wrapper";
import { UlmContainer } from "@components/ulm/ulm-container";
import { UlmButton } from "@components/ulm/ulm-button";
import { UlmInputLabel } from "@components/ulm/ulm-input-label";
import { KeyboardEvent, useCallback, useState } from "react";
import { UlmTextField } from "@components/ulm/ulm-text-field";
import { ContactSupport } from "@components/ulm/contact-support";
import ArrowLeftIcon from "@assets/icons/arrow-left_24px.svg?react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useErrorHandlerContext } from "@context-providers/error-handler/error-handler-context";
import { PasswordRecoverySuccess } from "@pages/password-recovery/password-recovery-success";
import { resetFetchedServers } from "@store/login/login-slice";
import { useAppDispatch } from "@store/store-helper";
import { QueryParams } from "@router/route-params";
import { PageTitle } from "@components/common/page-title";
import { PageSubtitle } from "@components/common/page-subtitle";
import { faroCoreApiClient } from "@api/core-api/core-api-utils";
import { EmailUtils } from "@stellar/utils";

/**
 * PasswordRecovery component provides a user interface for password recovery.
 * It allows users to enter their email address and request a password reset.
 */
export function PasswordRecovery(): JSX.Element {
  const [searchParams] = useSearchParams();
  const emailValue = searchParams.get(QueryParams.email);

  const dispatch = useAppDispatch();

  const { handleError } = useErrorHandlerContext();

  const [isEmailInputError, setIsEmailInputError] = useState<boolean>(false);
  const [isResendingEmail, setIsResendingEmail] = useState<boolean>(false);
  const [isRecoverPasswordWithSuccess, setIsRecoverPasswordWithSuccess] =
    useState<boolean>(false);
  const [email, setEmail] = useState<string>(emailValue ?? "");

  const recoverPassword = useCallback(async () => {
    if (!EmailUtils.isValidEmail(email)) {
      setIsEmailInputError(true);
      return;
    }

    setIsResendingEmail(true);

    const emailValue = email.trim();
    const resetPayload = {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by backend
      resetpw_email: emailValue,
    };

    try {
      await faroCoreApiClient.users.resetUserPassword(resetPayload);
      setIsRecoverPasswordWithSuccess(true);
      setIsResendingEmail(false);
    } catch (error) {
      handleError({
        id: `resetUserPassword-${Date.now().toString()}`,
        title: "Failed to request a password reset.",
        error,
        uiType: "toast",
      });
    }

    setIsEmailInputError(false);
  }, [email, handleError]);

  const onEmailKeyUp = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      event.stopPropagation();
      if (event.key === "Enter") {
        void recoverPassword();
      }
    },
    [recoverPassword]
  );

  if (isRecoverPasswordWithSuccess) {
    return <PasswordRecoverySuccess email={email} />;
  }

  return (
    <UlmContainer>
      <PageTitle>Forgot your password?</PageTitle>
      <PageSubtitle>
        Enter the email address you used during registration.
      </PageSubtitle>

      <Stack
        sx={{
          marginTop: "100px",
          width: "100%",
        }}
      >
        <UlmInputLabel title="Email" />
        <UlmTextField
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          type="email"
          name="email"
          autoComplete="email"
          isError={isEmailInputError}
          errorText={"Enter a valid email address"}
          placeholder={"name@company.com"}
          onKeyUp={onEmailKeyUp}
        />
      </Stack>

      <UlmButton
        onClick={recoverPassword}
        isLoading={isResendingEmail}
        isLoadingText="Resending..."
        sx={{
          marginTop: "8px",
        }}
      >
        Reset my password
      </UlmButton>

      <Link
        component={RouterLink}
        to="/"
        underline="hover"
        sx={{
          alignItems: "center",
          display: "flex",
          marginTop: "52px",
          gap: "10px",
        }}
        onClick={() => {
          dispatch(resetFetchedServers());
        }}
      >
        <ArrowLeftIcon color="currentColor" /> Return to log in screen
      </Link>

      <Box
        component="div"
        sx={{
          marginBottom: "10px",
          textAlign: "center",
          fontSize: "14px",
          flex: 1,
          alignContent: "end",
          marginTop: "20px",
        }}
      >
        Can’t log in? Contact <ContactSupport />
      </Box>
    </UlmContainer>
  );
}
